import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CustomerSelectionModal } from '../CustomerSelectionModal';
import ProductSelectionModal from '../ProductSelectionModal';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useNotifications } from '../../store/useStore';
import type { Database } from '../../types/database.types';
import { createInvoice, calculateInvoiceTotals } from '../../lib/invoiceUtils';
import { supabase } from '../../lib/supabase';
import { Package, Plus, Trash2 } from 'lucide-react';

type Customer = Database['public']['Tables']['customers']['Row'];
type Product = Database['public']['Tables']['products']['Row'];
type PaymentMethod = Database['public']['Tables']['payment_methods']['Row'];

interface ProformaInvoiceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
}

interface InvoiceItem {
  id: string;
  product_id: string | null;
  product: Product | null;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  discount_percentage: number;
  vat_rate: number;
}

export const ProformaInvoiceFormModal: React.FC<ProformaInvoiceFormModalProps> = ({
  isOpen,
  onClose,
  onCreated,
}) => {
  const { addNotification } = useNotifications();

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [items, setItems] = useState<InvoiceItem[]>([]);
  const [saving, setSaving] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [pendingQuantityValues, setPendingQuantityValues] = useState<Record<string, string>>({});
  const [pendingPriceValues, setPendingPriceValues] = useState<Record<string, string>>({});
  const [pendingDiscountValues, setPendingDiscountValues] = useState<Record<string, string>>({});
  const [pendingVatValues, setPendingVatValues] = useState<Record<string, string>>({});
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);

  useEffect(() => {
    const loadPaymentMethods = async () => {
      try {
        const { data, error } = await supabase
          .from('payment_methods')
          .select('*')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        setPaymentMethods(data || []);
      } catch (error: any) {
        console.error('Errore caricamento modalità pagamento:', error);
      }
    };

    if (isOpen) {
      loadPaymentMethods();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedCustomer(null);
    setInvoiceDate(new Date().toISOString().slice(0, 10));
    setItems([]);
    setPaymentTerms('');
    setNotes('');
    setPendingQuantityValues({});
    setPendingPriceValues({});
    setPendingDiscountValues({});
    setPendingVatValues({});
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleSave = async () => {
    if (!selectedCustomer) {
      addNotification({
        type: 'error',
        title: 'Cliente mancante',
        message: 'Seleziona un cliente per la fattura pro forma.',
      });
      return;
    }

    const validItems = items.filter(
      (item) => item.description.trim() && item.quantity > 0 && item.unit_price >= 0
    );

    if (validItems.length === 0) {
      addNotification({
        type: 'error',
        title: 'Prodotti mancanti',
        message: 'Aggiungi almeno un prodotto valido alla fattura pro forma.',
      });
      return;
    }

    setSaving(true);
    try {
      const invoiceItems = validItems.map((item) => ({
        product_id: item.product_id,
        description: item.description,
        quantity: item.quantity,
        unit: item.unit,
        unit_price: item.unit_price,
        discount_percentage: item.discount_percentage,
        vat_rate: item.vat_rate,
      }));

      await createInvoice({
        customer_id: selectedCustomer.id,
        order_id: null,
        invoice_date: invoiceDate,
        payment_terms: paymentTerms || selectedCustomer.payment_terms || null,
        notes: notes || null,
        status: 'draft', // Sempre bozza per pro forma
        document_type: 'proforma',
        items: invoiceItems,
      });

      addNotification({
        type: 'success',
        title: 'Documento creato',
        message: 'Il documento è stato creato correttamente.',
      });

      resetForm();
      onCreated();
      onClose();
    } catch (error: any) {
      console.error('Errore salvataggio fattura pro forma:', error);
      
      let errorMessage = 'Si è verificato un errore durante la creazione del documento.';
      
      if (error?.message) {
        errorMessage = error.message;
      } else if (error?.details) {
        errorMessage = error.details;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      addNotification({
        type: 'error',
        title: 'Errore creazione documento',
        message: errorMessage,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProductsSelected = (products: Product[]) => {
    const newItems: InvoiceItem[] = products.map(product => ({
      id: `item-${Date.now()}-${Math.random()}`,
      product_id: product.id,
      product: product,
      description: product.name + (product.description ? ` - ${product.description}` : ''),
      quantity: 1,
      unit: product.unit || 'pz',
      unit_price: product.base_price || 0,
      discount_percentage: 0,
      vat_rate: product.iva ?? 22,
    }));

    setItems(prev => [...prev, ...newItems]);
    setShowProductModal(false);
  };

  const handleDeleteItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  const handleQuantityChange = (id: string, quantity: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  };

  const handlePriceChange = (id: string, price: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, unit_price: price } : item
    ));
  };

  const handleDiscountChange = (id: string, discount: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, discount_percentage: discount } : item
    ));
  };

  const handleVatChange = (id: string, vat: number) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, vat_rate: vat } : item
    ));
  };

  const handleDescriptionChange = (id: string, description: string) => {
    setItems(prev => prev.map(item => 
      item.id === id ? { ...item, description } : item
    ));
  };

  const existingProductIds = useMemo(() => new Set(items.map(item => item.product_id).filter(Boolean) as string[]), [items]);

  const totals = useMemo(() => {
    const invoiceItems = items.map((item) => ({
      product_id: item.product_id,
      description: item.description || '',
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      discount_percentage: item.discount_percentage,
      vat_rate: item.vat_rate,
    }));
    return calculateInvoiceTotals(invoiceItems);
  }, [items]);

  const renderProductCard = (item: InvoiceItem) => {
    return (
      <div key={item.id} className="bg-white border border-green-200 rounded p-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3 sm:flex-wrap xl:flex-nowrap">
          <div className="flex items-center gap-2 w-full min-w-0 sm:min-w-[220px] sm:flex-1">
            {item.product?.photo_url && (
              <img
                src={item.product.photo_url}
                alt={item.product.name}
                className="w-8 h-8 object-cover rounded border"
              />
            )}
            <div className="flex-1">
              <Input
                value={item.description}
                onChange={(e) => handleDescriptionChange(item.id, e.target.value)}
                className="h-5 text-xs font-medium text-gray-900 mb-1"
                placeholder="Descrizione"
              />
              {item.product && (
                <p className="text-xs text-gray-500">{item.product.code}</p>
              )}
            </div>
          </div>

          {item.discount_percentage > 0 && (
            <div className="text-xs text-green-700 font-semibold min-w-[70px]">
              Sconto {item.discount_percentage}%
            </div>
          )}

          <div className="grid grid-cols-2 gap-2 sm:contents">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[140px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Quantità:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingQuantityValues[item.id] ?? item.quantity.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingQuantityValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingQuantityValues[item.id] ?? item.quantity.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue > 0 ? parsedValue : item.quantity;
                if (finalValue !== item.quantity) {
                  handleQuantityChange(item.id, finalValue);
                }
                setPendingQuantityValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">{item.unit}</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[130px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Prezzo:</span>
            <div className="relative flex-1 min-w-0 sm:flex-none">
              <Input
                type="text"
                inputMode="decimal"
                value={
                  pendingPriceValues[item.id] ??
                  (item.unit_price !== null && item.unit_price !== undefined
                    ? String(item.unit_price)
                    : '')
                }
                onChange={(e) => {
                  const value = e.target.value;
                  if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                    setPendingPriceValues(prev => ({
                      ...prev,
                      [item.id]: value,
                    }));
                  }
                }}
                onBlur={() => {
                  const raw =
                    pendingPriceValues[item.id] ??
                    (item.unit_price !== null && item.unit_price !== undefined
                      ? String(item.unit_price)
                      : '');

                  if (raw === '') {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const parsed = parseFloat(raw.replace(',', '.'));
                  if (isNaN(parsed)) {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                    return;
                  }

                  const normalized = Number(parsed.toFixed(2));
                  if (normalized !== Number(item.unit_price ?? 0)) {
                    handlePriceChange(item.id, normalized);
                  } else {
                    setPendingPriceValues(prev => {
                      const { [item.id]: _removed, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                className="h-5 text-xs w-full sm:w-24 pr-4 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <span className="absolute right-1 top-1/2 -translate-y-1/2 text-xs text-gray-500">€</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">Sconto:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingDiscountValues[item.id] ?? item.discount_percentage.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingDiscountValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingDiscountValues[item.id] ?? item.discount_percentage.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue >= 0 ? Math.min(parsedValue, 100) : item.discount_percentage;
                if (finalValue !== item.discount_percentage) {
                  handleDiscountChange(item.id, finalValue);
                }
                setPendingDiscountValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">%</span>
          </div>

          <div className="flex flex-wrap items-center gap-x-1 gap-y-0.5 sm:flex-nowrap sm:min-w-[100px]">
            <span className="text-xs text-gray-600 w-full sm:w-auto">IVA:</span>
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*[.,]?[0-9]*"
              value={pendingVatValues[item.id] ?? item.vat_rate.toString()}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*[.,]?[0-9]*$/.test(value) || value === '') {
                  setPendingVatValues(prev => ({
                    ...prev,
                    [item.id]: value
                  }));
                }
              }}
              onBlur={() => {
                const rawValue = pendingVatValues[item.id] ?? item.vat_rate.toString();
                const parsedValue = parseFloat(rawValue.replace(',', '.'));
                const finalValue = !isNaN(parsedValue) && parsedValue >= 0 ? parsedValue : item.vat_rate;
                if (finalValue !== item.vat_rate) {
                  handleVatChange(item.id, finalValue);
                }
                setPendingVatValues(prev => {
                  const { [item.id]: _removed, ...rest } = prev;
                  return rest;
                });
              }}
              className="h-5 text-xs flex-1 min-w-0 sm:flex-none sm:w-16 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-xs text-gray-500">%</span>
          </div>

          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDeleteItem(item.id)}
            className="h-8 sm:h-5 w-full sm:w-auto text-xs px-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
            <span className="ml-1 sm:hidden">Rimuovi riga</span>
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
        <DialogContent className="flex flex-col gap-3 overflow-hidden sm:max-h-[90vh] sm:max-w-6xl">
          <DialogHeader>
            <DialogTitle>Nuova Fattura Pro Forma</DialogTitle>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 py-2">
            {/* Cliente */}
            <div className="space-y-1">
              <Label>Cliente *</Label>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  readOnly
                  value={selectedCustomer?.company_name || 'Nessun cliente selezionato'}
                  className="bg-gray-50"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowCustomerModal(true)}
                >
                  Seleziona
                </Button>
              </div>
            </div>

            {/* Dati documento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Data documento *</Label>
                <Input
                  type="date"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label>Note</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Note aggiuntive (opzionale)"
                />
              </div>
            </div>

            {/* Modalità di pagamento */}
            <div className="space-y-1">
              <Label>Modalità di pagamento</Label>
              <Select
                value={paymentTerms || ''}
                onValueChange={setPaymentTerms}
              >
                <SelectTrigger>
                  <SelectValue placeholder={selectedCustomer?.payment_terms || "Seleziona modalità di pagamento"} />
                </SelectTrigger>
                <SelectContent>
                  {selectedCustomer?.payment_terms && !paymentMethods.some(pm => pm.name === selectedCustomer.payment_terms) && (
                    <SelectItem value={selectedCustomer.payment_terms}>
                      {selectedCustomer.payment_terms} (da cliente)
                    </SelectItem>
                  )}
                  {paymentMethods.map((method) => (
                    <SelectItem key={method.id} value={method.name}>
                      {method.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!paymentTerms && selectedCustomer?.payment_terms && (
                <p className="text-xs text-gray-500 mt-1">
                  Default cliente: {selectedCustomer.payment_terms}
                </p>
              )}
            </div>

            {/* Prodotti */}
            <div className="space-y-3 border rounded-md p-3 bg-green-50 border-green-200">
              <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                <div className="flex items-center space-x-2">
                  <Package className="w-5 h-5 text-green-600" />
                  <Label className="mb-0 text-lg font-semibold text-green-800">
                    Prodotti ({items.length})
                  </Label>
                </div>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => setShowProductModal(true)}
                  className="h-9 sm:h-7 text-xs px-3 bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Aggiungi Prodotti
                </Button>
              </div>

              {items.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <p className="text-sm text-green-700">
                    Aggiungi prodotti alla fattura pro forma
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {items.map(item => renderProductCard(item))}
                </div>
              )}

              {/* Totali */}
              {items.length > 0 && (
                <div className="pt-2 border-t border-green-300 text-xs text-gray-700 flex flex-wrap justify-end gap-x-4 gap-y-1">
                  <div>Imponibile: € {totals.taxable_amount.toFixed(2)}</div>
                  <div>IVA: € {totals.tax_amount.toFixed(2)}</div>
                  <div className="font-semibold">
                    Totale: € {totals.total_amount.toFixed(2)}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose} disabled={saving}>
              Annulla
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Salvataggio...' : 'Salva fattura pro forma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selezione cliente */}
      <CustomerSelectionModal
        isOpen={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onCustomerSelect={(c) => {
          setSelectedCustomer(c);
          // Imposta il payment_terms del cliente come default se disponibile
          if (c.payment_terms) {
            setPaymentTerms(c.payment_terms);
          } else {
            setPaymentTerms('');
          }
        }}
        selectedCustomerId={selectedCustomer?.id}
        title="Seleziona cliente per la fattura pro forma"
        description="Scegli il cliente a cui intestare la fattura pro forma"
      />
      
      {/* Selezione prodotti */}
      <ProductSelectionModal
        isOpen={showProductModal}
        onClose={() => setShowProductModal(false)}
        mode="select"
        onProductsSelected={handleProductsSelected}
        currentCustomer={selectedCustomer}
        existingProductIds={existingProductIds}
      />
    </>
  );
};
